/**
 * Tasks (owner, 12 Oct 2026): what the screens read from /tasks, and the one
 * summary query the menu, the reminder bar and the page share. Since 20 Oct
 * 2026 any employee can give a task to another, so the list has three views —
 * for me, I gave, and (the owner's desks) everyone's.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { givesTasks, isTaskAssignee, isTaskAssigner, type PersonRuleInput, type TaskCadence, type TaskPriority, type TaskStatus, type TaskView } from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export type TaskTab = 'open' | 'waiting' | 'overdue' | 'closed';

export interface TaskFile {
  id: string;
  name: string | null;
  mime: string;
  size: number;
}

export interface TaskRow {
  id: string;
  title: string;
  details: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  statusLabel: string;
  dueAt: string | null;
  overdue: boolean;
  assignee: { id: string; name: string };
  assignedBy: { id: string; name: string };
  repeats: string | null;
  createdAt: string;
  doneAt: string | null;
  closedAt: string | null;
  lastNote: { by: string | null; note: string; at: string } | null;
  followUps: number;
}

export interface TaskEvent {
  id: string;
  kind: 'CREATED' | 'COMMENT' | 'STARTED' | 'DONE' | 'CLOSED' | 'SENT_BACK' | 'CANCELLED' | 'REOPENED' | 'EDITED';
  note: string | null;
  at: string;
  by: { id: string; name: string } | null;
  files: TaskFile[];
}

export interface TaskDetail extends TaskRow {
  files: TaskFile[];
  events: TaskEvent[];
  can: { start: boolean; done: boolean; comment: boolean; close: boolean; sendBack: boolean; cancel: boolean; reopen: boolean; edit: boolean };
}

/** A card on the board, with the columns this sign-in may drag it to — the API's word, never guessed here. */
export interface TaskBoardRow extends TaskRow {
  moves: TaskStatus[];
}

export interface TaskBoardColumn {
  status: TaskStatus;
  /** Every task in the column; `rows` holds the first of them (Done: the newest). */
  total: number;
  rows: TaskBoardRow[];
}

export interface TaskBoard {
  view: TaskView;
  columns: TaskBoardColumn[];
  counts: { open: number; dueToday: number; overdue: number; closedThisWeek: number };
}

export interface TaskList {
  data: TaskRow[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
  tabs: Record<TaskTab, number>;
}

export interface TaskSummary {
  role: 'assigner' | 'assignee' | null;
  open: number;
  overdue: number;
  dueToday: number;
  waiting: number;
  /** Tasks this sign-in gave that are still open. */
  givenOpen: number;
  /** Tasks this sign-in gave that are now waiting for its check. */
  givenWaiting: number;
}

export interface Recurrence {
  id: string;
  title: string;
  details: string | null;
  priority: TaskPriority;
  assignee: { id: string; name: string };
  repeats: string;
  cadence: TaskCadence;
  weekday: number | null;
  monthDay: number | null;
  dueTime: string;
  isActive: boolean;
  /** On, but making nothing: the person's sign-in is off or no longer an employee's. */
  stalled: boolean;
  made: number;
  open: number;
}

export interface Assignee {
  id: string;
  name: string;
  username: string | null;
  role: string;
  book: string;
}

export const taskKeys = {
  all: ['tasks'] as const,
  summary: ['tasks', 'summary'] as const,
  list: (view: TaskView, tab: TaskTab, assigneeId: string, q: string, page: number) => ['tasks', 'list', view, tab, assigneeId, q, page] as const,
  board: (view: TaskView, assigneeId: string, q: string) => ['tasks', 'board', view, assigneeId, q] as const,
  detail: (id: string) => ['tasks', 'detail', id] as const,
  assignees: ['tasks', 'assignees'] as const,
  recurrences: ['tasks', 'recurrences'] as const,
};

/** Whether this sign-in has tasks at all: the owner who gives them, or an employee who does them. */
export function useTaskRole(): 'assigner' | 'assignee' | null {
  const { user } = useAuth();
  if (!user) return null;
  if (isTaskAssigner(user)) return 'assigner';
  // Another desk of a person (20 Oct 2026) has no tasks of its own: they are on the person's sign-in.
  return isTaskAssignee({ roleKey: user.role.key, email: user.email, seatOfId: user.seatOf?.id }) ? 'assignee' : null;
}

/** The board: its four columns and the tiles over them, in one read. */
export function useTaskBoard(view: TaskView, assigneeId: string, q: string) {
  const role = useTaskRole();
  return useQuery({
    queryKey: taskKeys.board(view, assigneeId, q),
    queryFn: () => api.get<TaskBoard>('/tasks/board', { view, ...(assigneeId ? { assigneeId } : {}), ...(q ? { q } : {}) }, { book: null }),
    enabled: role !== null,
    placeholderData: (previous) => previous,
    staleTime: 20_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The step a card dragged from one column to another takes — the same calls
 * the task's own buttons make. Back to To do is "send back" from the check
 * (it needs a word on what still needs doing) and "reopen" from Done.
 */
export function boardStep(from: TaskStatus, to: TaskStatus): 'start' | 'done' | 'close' | 'send-back' | 'reopen' | null {
  if (to === 'IN_PROGRESS') return 'start';
  if (to === 'WAITING_CHECK') return 'done';
  if (to === 'CLOSED') return 'close';
  if (to === 'OPEN') return from === 'WAITING_CHECK' ? 'send-back' : from === 'CLOSED' ? 'reopen' : null;
  return null;
}

/** The counts for the menu and the reminder bar — every book, refreshed while the app is open. */
export function useTaskSummary() {
  const role = useTaskRole();
  return useQuery({
    queryKey: taskKeys.summary,
    // Tasks belong to no book: read them whichever book the header is on.
    queryFn: () => api.get<TaskSummary>('/tasks/summary', undefined, { book: null }),
    enabled: role !== null,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The number the menu shows: for the owner, what waits for his check; for an
 * employee, what is still to do plus what they gave that now waits for them.
 */
export function taskBadgeCount(summary: TaskSummary | undefined): number {
  if (!summary?.role) return 0;
  return summary.role === 'assigner' ? summary.waiting : summary.open + summary.givenWaiting;
}

/**
 * The views of the list this sign-in has. "For me" only for someone who can be
 * given a task (the owner's desks cannot), "I gave" for every giver, and
 * "Everyone" for the owner's desks alone.
 */
export function taskScopesFor(person: PersonRuleInput | null | undefined): TaskView[] {
  if (!person || !givesTasks(person)) return [];
  return [...(isTaskAssignee(person) ? (['mine'] as const) : []), 'given', ...(isTaskAssigner(person) ? (['all'] as const) : [])];
}

/** Where the page opens: everyone's for the owner's desks, one's own to-do list for an employee. */
export function startScope(scopes: readonly TaskView[], asked?: string | null): TaskView {
  const wanted = scopes.find((scope) => scope === asked);
  if (wanted) return wanted;
  return scopes.includes('all') ? 'all' : (scopes[0] ?? 'mine');
}

/** The tab a view opens on: the to-do list for one's own, what waits for one's check for the rest. */
export function firstTab(scope: TaskView): TaskTab {
  return scope === 'mine' ? 'open' : 'waiting';
}

/** The views this sign-in has — empty for a counter's sign-in or a driver's. */
export function useTaskScopes(): TaskView[] {
  const { user } = useAuth();
  const roleKey = user?.role.key;
  const email = user?.email;
  const seatOfId = user?.seatOf?.id ?? null;
  return useMemo(() => taskScopesFor(roleKey && email ? { roleKey, email, seatOfId } : null), [roleKey, email, seatOfId]);
}

/** What needs this person in each view, for the number beside its name; nothing when it is nothing. */
export function scopeCount(scope: TaskView, summary: TaskSummary | undefined): number | undefined {
  if (!summary) return undefined;
  const count = scope === 'mine' ? summary.open : scope === 'given' ? summary.givenWaiting : summary.waiting;
  return count > 0 ? count : undefined;
}

/** "Today 18:00", "Tomorrow 10:00", "14 Oct 18:00" — in India's time. */
export function dueLabel(dueAt: string | null): string | null {
  if (!dueAt) return null;
  const at = new Date(dueAt);
  const ist = (d: Date) => new Date(d.getTime() + 330 * 60_000);
  const day = ist(at).toISOString().slice(0, 10);
  const today = ist(new Date()).toISOString().slice(0, 10);
  const tomorrow = ist(new Date(Date.now() + 86_400_000)).toISOString().slice(0, 10);
  const time = ist(at).toISOString().slice(11, 16);
  if (day === today) return `Today ${time}`;
  if (day === tomorrow) return `Tomorrow ${time}`;
  const [, m, d] = day.split('-').map(Number);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m! - 1];
  return `${d} ${month} ${time}`;
}
